<script setup>
import {
    NCard, NSpace, NH1, NH2, NH3, NP, NAlert,
    NDivider, NList, NListItem, NGradientText
} from 'naive-ui';
import { useI18n } from 'vue-i18n';
import { Shield, Eye, Lock, Database, UserCheck, Mail } from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

const lastUpdated = "January 1, 2025";

const sections = [
    {
        icon: Database,
        title: 'privacy.sections.dataCollection.title',
        content: 'privacy.sections.dataCollection.content',
        items: 'privacy.sections.dataCollection.items'
    },
    {
        icon: Lock,
        title: 'privacy.sections.dataUse.title',
        content: 'privacy.sections.dataUse.content',
        items: 'privacy.sections.dataUse.items'
    },
    {
        icon: Shield,
        title: 'privacy.sections.dataSecurity.title',
        content: 'privacy.sections.dataSecurity.content',
        items: 'privacy.sections.dataSecurity.items'
    },
    {
        icon: Eye,
        title: 'privacy.sections.dataSharing.title',
        content: 'privacy.sections.dataSharing.content',
        items: 'privacy.sections.dataSharing.items'
    },
    {
        icon: UserCheck,
        title: 'privacy.sections.userRights.title',
        content: 'privacy.sections.userRights.content',
        items: 'privacy.sections.userRights.items'
    }
];
</script>

<template>
    <div class="privacy-container">
        <!-- Hero Section -->
        <div class="hero-section">
            <NH1 class="hero-title">
                <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
                    {{ dt('privacy.heroTitle') }}
                </NGradientText>
            </NH1>
            <NP class="hero-subtitle">{{ dt('privacy.heroSubtitle') }}</NP>
            <NP class="last-updated">{{ dt('privacy.lastUpdated') }}: {{ lastUpdated }}</NP>
        </div>

        <!-- Important Notice -->
        <NAlert type="warning" class="disclaimer-alert">
            <template #icon>
                <Shield />
            </template>
            <strong>{{ dt('privacy.disclaimer.title') }}</strong>
            <NP style="margin-top: 8px;">{{ dt('privacy.disclaimer.content') }}</NP>
        </NAlert>

        <!-- Introduction -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.introduction.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.introduction.p1') }}</NP>
            <NP class="section-text">{{ dt('privacy.introduction.p2') }}</NP>
            <NP class="section-text">{{ dt('privacy.introduction.p3') }}</NP>
        </NCard>

        <!-- Main Sections -->
        <NCard v-for="(section, index) in sections" :key="index" class="content-card">
            <div class="section-header">
                <div class="section-icon" :style="{ 
                    background: `linear-gradient(135deg, ${['#36d1dc', '#5b86e5', '#ef473a', '#f7b733', '#a8edea'][index]}, ${['#5b86e5', '#ef473a', '#f7b733', '#a8edea', '#36d1dc'][index]})` 
                }">
                    <component :is="section.icon" size="24" color="white" />
                </div>
                <NH2 class="section-title">{{ dt(section.title) }}</NH2>
            </div>
            <NP class="section-text">{{ dt(section.content) }}</NP>
            <NList hoverable clickable class="section-list">
                <NListItem v-for="i in 5" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    {{ dt(`${section.items}.${i}`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Third Party Services -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.thirdParty.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.thirdParty.content') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 4" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    <strong>{{ dt(`privacy.thirdParty.services.${i}.name`) }}:</strong>
                    {{ dt(`privacy.thirdParty.services.${i}.desc`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Cookies and Tracking -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.cookies.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.cookies.content') }}</NP>
            <NList hoverable class="section-list">
                <NListItem v-for="i in 3" :key="i">
                    <template #prefix>
                        <div class="list-bullet"></div>
                    </template>
                    <strong>{{ dt(`privacy.cookies.types.${i}.name`) }}:</strong>
                    {{ dt(`privacy.cookies.types.${i}.desc`) }}
                </NListItem>
            </NList>
        </NCard>

        <!-- Data Retention -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.retention.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.retention.content') }}</NP>
            <NP class="section-text">{{ dt('privacy.retention.p2') }}</NP>
        </NCard>

        <!-- Children's Privacy -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.children.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.children.content') }}</NP>
        </NCard>

        <!-- Changes to Policy -->
        <NCard class="content-card">
            <NH2 class="section-title">{{ dt('privacy.changes.title') }}</NH2>
            <NP class="section-text">{{ dt('privacy.changes.content') }}</NP>
        </NCard>

        <!-- Contact Section -->
        <NCard class="content-card contact-card">
            <div class="contact-header">
                <Mail size="32" color="#36d1dc" />
                <NH2 class="section-title">{{ dt('privacy.contact.title') }}</NH2>
            </div>
            <NP class="section-text">{{ dt('privacy.contact.content') }}</NP>
            <div class="contact-info">
                <NP><strong>{{ dt('privacy.contact.email') }}:</strong> privacy@lawdosti.com</NP>
                <NP><strong>{{ dt('privacy.contact.address') }}:</strong> {{ dt('privacy.contact.addressValue') }}</NP>
            </div>
        </NCard>
    </div>
</template>

<style scoped>
.privacy-container {
    padding: 16px;
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    color: #333;
    overflow-x: hidden;
    box-sizing: border-box;
}

.hero-section {
    text-align: center;
    padding: 40px 0;
}

.hero-title {
    margin-bottom: 12px;
}

.hero-subtitle {
    font-size: 1.2rem;
    color: #555;
    margin-bottom: 8px;
}

.last-updated {
    font-size: 0.9rem;
    color: #888;
    font-style: italic;
}

.disclaimer-alert {
    margin: 30px 0;
    border-radius: 12px;
}

.content-card {
    margin: 24px 0;
    border-radius: 16px;
}

.section-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.section-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.section-title {
    margin-bottom: 16px;
    color: #333;
}

.section-text {
    margin-bottom: 16px;
    line-height: 1.8;
    color: #555;
    font-size: 1rem;
}

.section-list {
    margin-top: 16px;
}

.list-bullet {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.contact-card {
    background: linear-gradient(135deg, rgba(54, 209, 220, 0.05), rgba(91, 134, 229, 0.05));
}

.contact-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.contact-info {
    margin-top: 20px;
    padding: 20px;
    background: white;
    border-radius: 12px;
    border-left: 4px solid #36d1dc;
}

.contact-info p {
    margin: 8px 0;
}

@media screen and (max-width: 768px) {
    .hero-section {
        padding: 24px 0;
    }

    .section-header {
        flex-direction: column;
        text-align: center;
    }

    .section-icon {
        margin: 0 auto;
    }
}
</style>